import { useState, useEffect, useRef } from "react";
import { isValidEmail } from "@/lib/validations";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Bug, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePodgoVersion } from "@/hooks/usePodgoVersion";
import { submitBugReport, uploadBugScreenshot } from "@/services/bugReportService";
import {
  getBrowserInfo,
  getOSInfo,
  getDeviceType,
  getViewportInfo,
  getRecentConsoleErrors,
  getRecentFailedRequests,
  getRecentUserActions,
  captureScreenshot,
} from "@/lib/bugReportUtils";
import { ScreenshotUpload } from "./ScreenshotUpload";
import { supabase } from "@/integrations/supabase/client";

const CATEGORIES = [
  { value: "ui_issue", label: "UI / Visual Issue" },
  { value: "broken_feature", label: "Broken Feature" },
  { value: "data_issue", label: "Data Issue" },
  { value: "performance", label: "Performance" },
  { value: "other", label: "Other" },
];

const SEVERITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

interface BugReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Screenshot blob captured before the modal opened */
  preCapture?: Blob | null;
  /** URL of the page the user was on when they triggered the report */
  sourcePageUrl?: string;
  /** Optional pre-filled description (e.g. from crash recovery) */
  initialDescription?: string;
  /** Whether this modal was opened as part of crash recovery */
  isCrashRecovery?: boolean;
}

export function BugReportModal({ open, onOpenChange, preCapture: externalCapture, sourcePageUrl, initialDescription, isCrashRecovery }: BugReportModalProps) {
  const { user, isAuthenticated } = useAuth();
  const { version } = usePodgoVersion();

  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [autoCapture, setAutoCapture] = useState<Blob | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageUrl, setPageUrl] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [reportRef, setReportRef] = useState("");
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-fill email for logged-in users; seed initialDescription on fresh open
  useEffect(() => {
    if (open && user?.email) {
      setEmail(user.email);
    }
    if (open) {
      setPageUrl(sourcePageUrl || window.location.href);
      if (initialDescription && !description) {
        setDescription(initialDescription);
      }
    }
  }, [open, user, sourcePageUrl, initialDescription]);

  // Cleanup countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Use pre-captured screenshot when provided; fall back to in-place capture
  useEffect(() => {
    if (!open) return;
    if (externalCapture) {
      setAutoCapture(externalCapture);
      return;
    }
    // Fallback: capture in-place (backward compat for callers without preCapture)
    const timer = setTimeout(async () => {
      const blob = await captureScreenshot();
      if (blob) setAutoCapture(blob);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, externalCapture]);

  const resetForm = () => {
    setEmail(user?.email || "");
    setCategory("");
    setSeverity("medium");
    setDescription("");
    setScreenshots([]);
    setAutoCapture(null);
    setHoneypot("");
    setShowSuccess(false);
    setCountdown(5);
    setReportRef("");
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) return;

    if (!email || !category || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (description.trim().length < 20) {
      toast.error("Please provide a more detailed description (at least 20 characters)");
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload screenshots to R2 — returns the cdn.podgo.io public URL.
      const screenshotUrls: string[] = [];

      if (autoCapture) {
        const url = await uploadBugScreenshot(autoCapture, "auto-capture.png");
        screenshotUrls.push(url);
      }

      for (const file of screenshots) {
        const url = await uploadBugScreenshot(file, file.name);
        screenshotUrls.push(url);
      }

      // Get subscription plan if logged in
      let subscriptionPlan: string | null = null;
      if (user) {
        try {
          const { data: sub } = await supabase
            .from("subscriptions")
            .select("plan")
            .eq("user_id", user.id)
            .single();
          subscriptionPlan = sub?.plan || null;
        } catch {
          // Ignore
        }
      }

      const reportData = {
        user_id: user?.id || null,
        email,
        category,
        severity,
        description: description.trim(),
        page_url: pageUrl,
        browser: getBrowserInfo(),
        os: getOSInfo(),
        device_type: getDeviceType(),
        viewport: getViewportInfo(),
        console_errors: getRecentConsoleErrors(),
        app_version: version,
        subscription_plan: subscriptionPlan,
        screenshots: screenshotUrls,
        honeypot,
        session_metadata: {
          failed_requests: getRecentFailedRequests(),
          user_actions: getRecentUserActions(),
        },
      };

      const { id } = await submitBugReport(reportData);

      // Notify admins (fire and forget)
      try {
        await supabase.functions.invoke("notify-bug-report", {
          body: {
            bugReportId: id,
            category,
            severity,
            reporterEmail: email,
            pageUrl,
            description: description.trim().substring(0, 200),
          },
        });
      } catch {
        // Silent fail
      }

      // Crash recovery: show inline success with countdown
      if (isCrashRecovery) {
        setReportRef(`BR-${id.substring(0, 8)}`);
        setShowSuccess(true);
        setCountdown(5);
        countdownRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              if (countdownRef.current) clearInterval(countdownRef.current);
              countdownRef.current = null;
              window.location.href = "/dashboard";
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.success("Bug report submitted", {
          description: `Reference: BR-${id.substring(0, 8)} — We'll investigate and follow up.`,
        });
        resetForm();
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toast.error("Failed to submit bug report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToDashboard = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    window.location.href = "/dashboard";
  };

  return (
    <Dialog open={open} onOpenChange={showSuccess ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onInteractOutside={showSuccess ? (e) => e.preventDefault() : undefined}>
        {showSuccess ? (
          <div className="flex flex-col items-center text-center py-8 space-y-4">
            <CheckCircle className="h-12 w-12 text-success" />
            <h2 className="text-xl font-semibold text-foreground">Report submitted successfully</h2>
            <p className="text-sm text-muted-foreground">
              We captured some technical details to help investigate this issue.
            </p>
            <p className="text-xs font-mono text-muted-foreground">{reportRef}</p>
            <p className="text-sm text-muted-foreground">
              You'll be redirected to the dashboard in {countdown} second{countdown !== 1 ? "s" : ""}.
            </p>
            <Button onClick={goToDashboard} className="mt-2">
              Go to Dashboard
            </Button>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-accent" />
            Report a Bug
          </DialogTitle>
          <DialogDescription>
            Help us improve PODGO by reporting issues you encounter.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot — hidden from humans */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="bug-email" className="text-sm font-medium">
              Email <span className="text-destructive">*</span>
            </label>
            <Input
              id="bug-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5"
              required
              readOnly={isAuthenticated}
            />
          </div>

          {/* Category & Severity row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">
                Category <span className="text-destructive">*</span>
              </label>
              <Select value={category} onValueChange={setCategory} required>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">
                Severity <span className="text-destructive">*</span>
              </label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="bug-desc" className="text-sm font-medium">
              Description <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="bug-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5 min-h-[120px]"
              placeholder="Describe the issue you encountered...&#10;&#10;What were you trying to do?&#10;What happened instead?&#10;Steps to reproduce (if possible)..."
              required
              minLength={20}
            />
          </div>

          {/* Screenshots */}
          <ScreenshotUpload
            screenshots={screenshots}
            autoCapture={autoCapture}
            onScreenshotsChange={setScreenshots}
            onRemoveAutoCapture={() => setAutoCapture(null)}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !category || !description.trim()}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Report
            </Button>
          </DialogFooter>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
